import { isObject } from '@scalar/helpers/object/is-object';
import { getResolvedRef } from '@scalar/workspace-store/helpers/get-resolved-ref';
import { unpackProxyShallow } from '@scalar/workspace-store/helpers/unpack-proxy';
/**
 * Recursively resolves all $ref objects in a data structure to their actual values.
 * Traverses through objects, arrays, and nested structures to find and resolve
 * any $ref references at any depth level.
 *
 * Handles circular references gracefully by detecting them and returning '[circular]'
 * to prevent infinite loops.
 */
export const getResolvedRefDeep = (node) => {
    const visited = new WeakSet();
    const cachedResults = new WeakMap();
    const resolveNode = (current) => {
        // Only recurse into objects and arrays; return primitives as-is
        if (!isObject(current) && !Array.isArray(current)) {
            return current;
        }
        // Identity only: the raw object keys the caches below and is never read from or returned, so the
        // outermost proxies come off and the node's own properties are left alone.
        const rawValue = unpackProxyShallow(current);
        // We don't have to recurse into the same object again
        // This helps us having to manually remove the tracked node after we recurse into the tree
        if (cachedResults.has(rawValue)) {
            return cachedResults.get(rawValue);
        }
        // Break circular references
        if (visited.has(rawValue)) {
            return '[circular]';
        }
        // Track visited nodes
        visited.add(rawValue);
        if ('$ref' in current) {
            // `getResolvedRef` follows a chain of pure pass-through references, so a component that resolve()
            // left behind as a `$global` stub reaches the node the stub points at rather than the stub.
            const resolved = getResolvedRef(current);
            const result = resolveNode(resolved);
            // Preserve keywords declared alongside the `$ref`. A `$ref` may carry siblings that specialize the
            // target — most notably the `$defs`/`$dynamicAnchor` binding used to express a generic schema like
            // `Paginated<Planet>`. Per OpenAPI 3.1, siblings of a `$ref` override the resolved value, so we merge
            // them on top. Without this the dynamic-ref binding is dropped and `$dynamicRef` cannot resolve.
            let merged = undefined;
            if (isObject(result)) {
                for (const key of Object.keys(current)) {
                    if (key === '$ref' || key === '$ref-value') {
                        continue;
                    }
                    merged ??= { ...result };
                    merged[key] = resolveNode(current[key]);
                }
            }
            const value = merged ?? result;
            cachedResults.set(rawValue, value);
            return value;
        }
        // For arrays
        if (Array.isArray(current)) {
            const result = new Array(current.length);
            for (let index = 0; index < current.length; index++) {
                result[index] = resolveNode(current[index]);
            }
            cachedResults.set(rawValue, result);
            return result;
        }
        const result = {};
        for (const key of Object.keys(current)) {
            result[key] = resolveNode(current[key]);
        }
        cachedResults.set(rawValue, result);
        return result;
    };
    return resolveNode(node);
};
